import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../context/AuthContext';
import { formatDate, parseTableParams } from '../../utils/Utils';
import api from '../../services/api';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import PersonCell from '../../components/ui/PersonCell';
import { dateFilterColumn } from '../../utils/dateFilterColumn';

const Container = styled.div``;

export default function AssetAssignmentsPage() {
  const { t, i18n } = useTranslation();
  const { canWrite } = useAuth();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [queryParams, setQueryParams] = useState({ pageNumber: 1, pageSize: 10 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [returnConfirm, setReturnConfirm] = useState({ isOpen: false, id: null });
  const [returning, setReturning] = useState(false);
  const [filterList, setFilterList] = useState([]);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/asset-assignments?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const handleFilterConfirm = useCallback((fl) => setFilterList(fl), []);

  const searchAssets = useCallback(async (query) => {
    const params = query ? { 'name[like]': query, 'status[eq]': 'available', pageSize: 20 } : { 'status[eq]': 'available', pageSize: 20 };
    const res = await api.get('/v1/assets', { params });
    const result = res.data?.data;
    const items = Array.isArray(result) ? result : (result?.data || []);
    return items.map((a) => ({
      id: a.id,
      name: `${a.name}${a.barcodeNumber ? ` (${a.barcodeNumber})` : ''}`,
    }));
  }, []);

  const searchUsers = useCallback(async (query) => {
    const params = query ? { 'firstName[like]': query, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
    const result = res.data?.data;
    const items = Array.isArray(result) ? result : (result?.data || []);
    return items.map((u) => ({
      id: u.userId || u.id,
      name: `${u.firstName || ''} ${u.lastName || ''}`.trim(),
      profilePicture: u.profilePicture,
    }));
  }, []);

  const handleSave = useCallback(async (formData) => {
    try {
      await api.post('/v1/asset-assignments', {
        assetId: formData.assetId,
        userId: formData.userId,
        assignmentDate: formData.assignmentDate,
        notes: formData.notes || null,
      });
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const handleReturn = useCallback(async () => {
    setReturning(true);
    try {
      await api.put(`/v1/asset-assignments/${returnConfirm.id}/return`);
      setReturnConfirm({ isOpen: false, id: null });
      setRefreshTrigger((p) => p + 1);
    } catch { /* interceptor */ }
    setReturning(false);
  }, [returnConfirm.id]);

  const actionOptions = useMemo(() => {
    if (!canWrite('AM')) return [];
    return [
      { type: 'delete', label: t('assetAssignment.return'), onClick: (row) => setReturnConfirm({ isOpen: true, id: row.id }), condition: (row) => !row.returnDate },
    ];
  }, [t, canWrite]);

  const schema = useMemo(() => [
    {
      name: 'assetId', label: t('assetAssignment.assetName'), type: 'asyncSelect', grid: 12, required: true,
      onSearch: searchAssets,
    },
    {
      name: 'userId', label: t('assetAssignment.userFullName'), type: 'asyncSelect', grid: 12, required: true,
      onSearch: searchUsers,
      renderOption: (o) => <PersonCell name={o.name} profilePicture={o.profilePicture} />,
    },
    { name: 'assignmentDate', label: t('assetAssignment.assignmentDate'), type: 'date', grid: 12, required: true },
    { name: 'notes', label: t('assetAssignment.notes'), type: 'textarea', grid: 12 },
  ], [t, searchAssets, searchUsers]);

  const columns = useMemo(() => {
    const cols = [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'userFullName', label: t('assetAssignment.userFullName'),
        options: {
          filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' },
          customBodyRender: (v, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <PersonCell name={v || '-'} profilePicture={row?.userProfilePicture} />;
          },
        },
      },
      { name: 'assetName', label: t('assetAssignment.assetName'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
      dateFilterColumn('assignmentDate', t('assetAssignment.assignmentDate'), lang),
      dateFilterColumn('returnDate', t('assetAssignment.returnDate'), lang),
      { name: 'notes', label: t('assetAssignment.notes'), options: { filter: false, sort: false } },
    ];

    if (actionOptions.length > 0) {
      cols.push({
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (row?.returnDate) return null;
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      });
    }

    return cols;
  }, [t, data, actionOptions, lang]);

  return (
    <Container>
      <ConfirmDialog
        isOpen={returnConfirm.isOpen}
        title={t('assetAssignment.returnTitle')}
        message={t('assetAssignment.returnMessage')}
        onConfirm={handleReturn}
        onCancel={() => setReturnConfirm({ isOpen: false, id: null })}
        loading={returning}
      />

      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('assetAssignment.new')}
        schema={schema}
        onSave={handleSave}
      />

      <DataTable
        title={t('assetAssignment.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        canCreateNewRecord={canWrite('AM')}
        onNewButtonClick={() => setDrawerOpen(true)}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={(action, tableState) => {
          setFilterList(tableState.filterList);
          setQueryParams(parseTableParams(tableState, columns));
        }}
      />
    </Container>
  );
}
